import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Database, Download, Clock, HardDrive, RefreshCw, AlertTriangle, 
  Upload, Trash2, Cloud, CloudOff, CheckCircle, FileJson, 
  BarChart3, Users, Truck, ShoppingCart, CreditCard, History, Filter, User, Calendar,
  Send, MessageSquare, Bell, BellOff
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { tr } from "date-fns/locale";



const Backup = () => {
  const { isAdmin } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dropboxUploading, setDropboxUploading] = useState(false);
  
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [dropboxAuthDialogOpen, setDropboxAuthDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [dropboxAuthCode, setDropboxAuthCode] = useState("");
  const [dropboxAuthUrl, setDropboxAuthUrl] = useState("");
  
  const [dropboxStatus, setDropboxStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    interval_hours: 3,
    dropbox_enabled: false
  });
  
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Activity logs state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activitySummary, setActivitySummary] = useState(null);
  const [activityFilter, setActivityFilter] = useState({
    action: "",
    entity_type: "",
    limit: 50,
  });
  
  // Telegram state
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramSettings, setTelegramSettings] = useState({
    notifications_enabled: false,
    daily_report_enabled: false,
    monthly_report_enabled: false,
    daily_report_time: "19:00",
  });
  const [telegramLoading, setTelegramLoading] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchAll();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchAll = async () => {
    try {
      const [backupsRes, statsRes, dropboxRes, settingsRes, logsRes, logSummaryRes, telegramRes, telegramSettingsRes] = await Promise.all([
        axios.get(`${API}/backups`),
        axios.get(`${API}/data/statistics`),
        axios.get(`${API}/backup/dropbox/status`),
        axios.get(`${API}/backup/settings`),
        axios.get(`${API}/activity-logs?limit=50`),
        axios.get(`${API}/activity-logs/summary`),
        axios.get(`${API}/telegram/status`),
        axios.get(`${API}/telegram/settings`),
      ]);
      setBackups(backupsRes.data);
      setStatistics(statsRes.data);
      setDropboxStatus(dropboxRes.data);
      setBackupSettings(settingsRes.data);
      setActivityLogs(logsRes.data);
      setActivitySummary(logSummaryRes.data);
      setTelegramStatus(telegramRes.data);
      setTelegramSettings(telegramSettingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/backup/manual`);
      toast.success("Yedek başarıyla oluşturuldu");
      fetchAll();
    } catch (error) {
      console.error("Failed to create backup:", error);
      toast.error("Yedek oluşturulamadı");
    } finally {
      setCreating(false);
      setConfirmDialogOpen(false);
    }
  };

  const downloadBackup = async (backupId, filename) => {
    try {
      const response = await axios.get(`${API}/backups/${backupId}/download`);
      const blob = new Blob([response.data.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Yedek indirildi");
    } catch (error) {
      console.error("Failed to download backup:", error);
      toast.error("Yedek indirilemedi");
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/data/export`);
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Veriler dışa aktarıldı");
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportDialogOpen(true);
    }
  };

  const importData = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    try {
      const text = await selectedFile.text();
      const data = JSON.parse(text);
      
      const response = await axios.post(`${API}/data/import`, { data });
      
      if (response.data.success) {
        toast.success("Veriler başarıyla içe aktarıldı");
        fetchAll();
      } else {
        toast.error("İçe aktarma hatası: " + (response.data.errors?.join(", ") || "Bilinmeyen hata"));
      }
    } catch (error) {
      console.error("Failed to import:", error);
      toast.error("İçe aktarma başarısız: " + (error.message || "Geçersiz dosya formatı"));
    } finally {
      setImporting(false);
      setImportDialogOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteAllData = async () => {
    if (deleteConfirmText !== "ONAYLA") {
      toast.error("Silme işlemi için 'ONAYLA' yazmalısınız");
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/data/delete-all?confirm=ONAYLA`);
      toast.success("Tüm veriler silindi");
      fetchAll();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeleteConfirmText("");
    }
  };

  const backupToDropbox = async () => {
    setDropboxUploading(true);
    try {
      const response = await axios.post(`${API}/backup/dropbox`);
      toast.success(`Yedek Dropbox&apos;a yüklendi: ${response.data.path}`);
      fetchAll();
    } catch (error) {
      console.error("Failed to backup to Dropbox:", error);
      toast.error(error.response?.data?.detail || "Dropbox yedekleme başarısız");
    } finally {
      setDropboxUploading(false);
    }
  };

  const openDropboxAuth = async () => {
    try {
      const response = await axios.get(`${API}/backup/dropbox/auth-url`);
      setDropboxAuthUrl(response.data.auth_url);
      setDropboxAuthDialogOpen(true);
    } catch (error) {
      toast.error("Dropbox yetkilendirme URL'si alınamadı");
    }
  };

  const submitDropboxAuth = async () => {
    if (!dropboxAuthCode.trim()) {
      toast.error("Yetkilendirme kodu gerekli");
      return;
    }
    
    try {
      await axios.post(`${API}/backup/dropbox/auth`, { code: dropboxAuthCode.trim() });
      toast.success("Dropbox bağlantısı başarılı!");
      setDropboxAuthDialogOpen(false);
      setDropboxAuthCode("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yetkilendirme başarısız");
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/backup/settings`, newSettings);
      setBackupSettings(newSettings);
      toast.success("Ayarlar güncellendi");
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.error("Ayarlar güncellenemedi");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", activityFilter.limit);
      if (activityFilter.action) params.append("action", activityFilter.action);
      if (activityFilter.entity_type) params.append("entity_type", activityFilter.entity_type);
      
      const response = await axios.get(`${API}/activity-logs?${params.toString()}`);
      setActivityLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && !loading) {
      fetchActivityLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityFilter, isAdmin, loading]);

  const getActionLabel = (action) => {
    const labels = {
      create: "Oluşturma",
      update: "Güncelleme",
      delete: "Silme",
      login: "Giriş",
      logout: "Çıkış",
      backup: "Yedekleme",
      import: "İçe Aktarma",
      export: "Dışa Aktarma",
    };
    return labels[action] || action;
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      sale: "Satış",
      purchase: "Alış",
      service_purchase: "Hizmet Alışı",
      collection: "Tahsilat",
      payment: "Ödeme",
      offset: "Mahsup",
      expense: "Gider",
      customer: "Müşteri",
      supplier: "Tedarikçi",
      cash_account: "Kasa",
      bank_account: "Banka",
      credit_card: "Kredi Kartı",
      credit_card_payment: "KK Ödemesi",
      credit_card_statement: "KK Ekstresi",
      agenda: "Ajanda",
      user: "Kullanıcı",
    };
    return labels[entityType] || entityType;
  };

  const getActionColor = (action) => {
    const colors = {
      create: "bg-emerald-100 text-emerald-700",
      update: "bg-blue-100 text-blue-700",
      delete: "bg-red-100 text-red-700",
      login: "bg-purple-100 text-purple-700",
      backup: "bg-amber-100 text-amber-700",
      import: "bg-cyan-100 text-cyan-700",
      export: "bg-indigo-100 text-indigo-700",
    };
    return colors[action] || "bg-slate-100 text-slate-700";
  };

  // Telegram functions
  const handleTelegramTest = async () => {
    setTelegramLoading(true);
    try {
      await axios.post(`${API}/telegram/test`);
      toast.success("Test mesajı gönderildi! Telegram'ı kontrol edin.");
    } catch (error) {
      toast.error("Test mesajı gönderilemedi");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramSettingsUpdate = async (newSettings) => {
    setTelegramLoading(true);
    try {
      await axios.post(`${API}/telegram/settings`, newSettings);
      setTelegramSettings(newSettings);
      toast.success("Telegram ayarları güncellendi");
    } catch (error) {
      toast.error("Ayarlar güncellenemedi");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleSendDailyReport = async () => {
    setTelegramLoading(true);
    try {
      await axios.post(`${API}/telegram/send-daily-report`);
      toast.success("Günlük rapor gönderildi");
    } catch (error) {
      toast.error("Rapor gönderilemedi");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleSendMonthlyReport = async () => {
    setTelegramLoading(true);
    try {
      await axios.post(`${API}/telegram/send-monthly-report`);
      toast.success("Aylık rapor gönderildi");
    } catch (error) {
      toast.error("Rapor gönderilemedi");
    } finally {
      setTelegramLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="backup-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Veri Yönetimi
          </h1>
          <p className="text-slate-500 mt-1">Yedekleme ve veri yönetimi işlemleri</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-slate-700 font-medium">Yetki Gerekli</p>
            <p className="text-slate-500 mt-2">Bu sayfaya erişim için yönetici yetkisi gereklidir.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Veri Yönetimi
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Yedekleme, içe/dışa aktarma ve veri yönetimi</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="bg-blue-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-xs text-blue-600">Müşteri</p>
                  <p className="text-lg font-bold text-blue-700">{statistics.customers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-amber-600" />
                <div>
                  <p className="text-xs text-amber-600">Tedarikçi</p>
                  <p className="text-lg font-bold text-amber-700">{statistics.suppliers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-emerald-600" />
                <div>
                  <p className="text-xs text-emerald-600">Satış</p>
                  <p className="text-lg font-bold text-emerald-700">{statistics.sales}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-red-600" />
                <div>
                  <p className="text-xs text-red-600">Alış</p>
                  <p className="text-lg font-bold text-red-700">{statistics.purchases}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-purple-600" />
                <div>
                  <p className="text-xs text-purple-600">Gider</p>
                  <p className="text-lg font-bold text-purple-700">{statistics.expenses}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-100">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-slate-600" />
                <div>
                  <p className="text-xs text-slate-600">Toplam</p>
                  <p className="text-lg font-bold text-slate-700">{statistics.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Export */}
        <Card className="border-emerald-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-base">Dışa Aktar</CardTitle>
            </div>
            <CardDescription className="text-xs">Tüm verileri JSON olarak indir</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={exportData} 
              disabled={exporting}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {exporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {exporting ? "İndiriliyor..." : "Verileri İndir"}
            </Button>
          </CardContent>
        </Card>

        {/* Import */}
        <Card className="border-blue-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-base">İçe Aktar</CardTitle>
            </div>
            <CardDescription className="text-xs">JSON dosyasından verileri yükle</CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {importing ? "Yükleniyor..." : "Dosya Seç"}
            </Button>
          </CardContent>
        </Card>

        {/* Dropbox Backup */}
        <Card className={`border-2 ${dropboxStatus?.connected ? 'border-blue-400' : 'border-slate-200'}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {dropboxStatus?.connected ? (
                  <Cloud className="h-5 w-5 text-blue-500" />
                ) : (
                  <CloudOff className="h-5 w-5 text-slate-400" />
                )}
                <CardTitle className="text-base">Dropbox</CardTitle>
              </div>
              {dropboxStatus?.connected && (
                <CheckCircle className="h-4 w-4 text-emerald-500" />
              )}
            </div>
            <CardDescription className="text-xs">
              {dropboxStatus?.connected 
                ? `Bağlı: ${dropboxStatus.account_name}` 
                : "Bağlantı yok"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {dropboxStatus?.connected ? (
              <Button 
                onClick={backupToDropbox}
                disabled={dropboxUploading}
                className="w-full"
              >
                {dropboxUploading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Cloud className="h-4 w-4 mr-2" />
                )}
                {dropboxUploading ? "Yükleniyor..." : "Dropbox&apos;a Yedekle"}
              </Button>
            ) : (
              <Button 
                onClick={openDropboxAuth}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Cloud className="h-4 w-4 mr-2" />
                Dropbox&apos;a Bağlan
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Delete All */}
        <Card className="border-red-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              <CardTitle className="text-base">Verileri Sil</CardTitle>
            </div>
            <CardDescription className="text-xs">Tüm verileri kalıcı olarak sil</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => setDeleteDialogOpen(true)}
              variant="destructive"
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Tümünü Sil
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Telegram Settings */}
      <Card className="border-blue-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              <div>
                <CardTitle className="text-lg">Telegram Bildirimleri</CardTitle>
                <CardDescription className="text-sm">
                  İşlem bildirimleri ve raporlar
                </CardDescription>
              </div>
            </div>
            {telegramStatus?.connected ? (
              <span className="flex items-center gap-1 text-sm text-emerald-600">
                <CheckCircle className="h-4 w-4" />
                @{telegramStatus.bot_username}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                Bağlı değil
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Notification Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {telegramSettings.notifications_enabled ? (
                <Bell className="h-5 w-5 text-emerald-600" />
              ) : (
                <BellOff className="h-5 w-5 text-slate-400" />
              )}
              <div>
                <p className="font-medium">Anlık Bildirimler</p>
                <p className="text-sm text-slate-500">Her işlemde Telegram&apos;a bildirim gönder</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={telegramSettings.notifications_enabled}
                onChange={(e) => handleTelegramSettingsUpdate({
                  ...telegramSettings,
                  notifications_enabled: e.target.checked
                })}
                className="sr-only peer"
                disabled={telegramLoading}
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Daily Report Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">Günlük Rapor</p>
                <p className="text-sm text-slate-500">Her gün saat 19:00&apos;da özet rapor</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={telegramSettings.daily_report_enabled}
                  onChange={(e) => handleTelegramSettingsUpdate({
                    ...telegramSettings,
                    daily_report_enabled: e.target.checked
                  })}
                  className="sr-only peer"
                  disabled={telegramLoading}
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Monthly Report Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-purple-600" />
              <div>
                <p className="font-medium">Aylık Rapor</p>
                <p className="text-sm text-slate-500">Her ayın son günü karşılaştırmalı rapor</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={telegramSettings.monthly_report_enabled}
                onChange={(e) => handleTelegramSettingsUpdate({
                  ...telegramSettings,
                  monthly_report_enabled: e.target.checked
                })}
                className="sr-only peer"
                disabled={telegramLoading}
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-4">
            <Button
              onClick={handleTelegramTest}
              variant="outline"
              disabled={telegramLoading || !telegramStatus?.connected}
              className="w-full"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Test Mesajı
            </Button>
            <Button
              onClick={handleSendDailyReport}
              variant="outline"
              disabled={telegramLoading || !telegramStatus?.connected}
              className="w-full"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Günlük Rapor
            </Button>
            <Button
              onClick={handleSendMonthlyReport}
              variant="outline"
              disabled={telegramLoading || !telegramStatus?.connected}
              className="w-full"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Aylık Rapor
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Backup Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Otomatik Yedekleme Ayarları</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium">Otomatik Yedekleme</p>
              <p className="text-sm text-slate-500">Belirlenen aralıklarla otomatik yedek al</p>
            </div>
            <Switch
              checked={backupSettings.enabled}
              onCheckedChange={(checked) => updateSettings({ ...backupSettings, enabled: checked })}
            />
          </div>
          
          {backupSettings.enabled && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Yedekleme Aralığı</p>
                <p className="text-sm text-slate-500">Ne sıklıkla yedek alınsın?</p>
              </div>
              <Select
                value={String(backupSettings.interval_hours)}
                onValueChange={(val) => updateSettings({ ...backupSettings, interval_hours: parseInt(val) })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Her 1 saat</SelectItem>
                  <SelectItem value="3">Her 3 saat</SelectItem>
                  <SelectItem value="6">Her 6 saat</SelectItem>
                  <SelectItem value="12">Her 12 saat</SelectItem>
                  <SelectItem value="24">Her 24 saat</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {backupSettings.enabled && dropboxStatus?.connected && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-blue-50 rounded-lg">
              <div>
                <p className="font-medium">Dropbox&apos;a Otomatik Yedekle</p>
                <p className="text-sm text-slate-500">Yedekleri otomatik olarak Dropbox&apos;a yükle</p>
              </div>
              <Switch
                checked={backupSettings.dropbox_enabled}
                onCheckedChange={(checked) => updateSettings({ ...backupSettings, dropbox_enabled: checked })}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Backup Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="text-lg">Yedek Listesi</CardTitle>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={creating}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {creating ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              {creating ? "Oluşturuluyor..." : "Manuel Yedek Al"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {backups.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Dosya Adı</TableHead>
                    <TableHead className="text-xs">Tür</TableHead>
                    <TableHead className="text-xs">Boyut</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Tarih</TableHead>
                    <TableHead className="text-xs text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.id}>
                      <TableCell className="font-mono text-xs">{backup.filename}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          backup.type === "auto" ? "bg-blue-100 text-blue-700" : 
                          backup.type === "dropbox" ? "bg-sky-100 text-sky-700" :
                          "bg-purple-100 text-purple-700"
                        }`}>
                          {backup.type === "auto" ? "Otomatik" : backup.type === "dropbox" ? "Dropbox" : "Manuel"}
                        </span>
                      </TableCell>
                      <TableCell className="tabular-nums text-sm">{formatFileSize(backup.size)}</TableCell>
                      <TableCell className="text-sm hidden sm:table-cell">
                        {formatDistanceToNow(new Date(backup.created_at), { addSuffix: true, locale: tr })}
                      </TableCell>
                      <TableCell className="text-right">
                        {backup.type !== "dropbox" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadBackup(backup.id, backup.filename)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Henüz yedek oluşturulmamış</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Logs Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-600" />
              <div>
                <CardTitle className="text-lg">İşlem Geçmişi</CardTitle>
                {activitySummary && (
                  <p className="text-sm text-slate-500 mt-1">
                    Toplam {activitySummary.total_count} işlem • Bugün {activitySummary.today_count} işlem
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select
                value={activityFilter.action}
                onValueChange={(val) => setActivityFilter({ ...activityFilter, action: val === "all" ? "" : val })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="İşlem Türü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="create">Oluşturma</SelectItem>
                  <SelectItem value="update">Güncelleme</SelectItem>
                  <SelectItem value="delete">Silme</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={activityFilter.entity_type}
                onValueChange={(val) => setActivityFilter({ ...activityFilter, entity_type: val === "all" ? "" : val })}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Kayıt Türü" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="sale">Satış</SelectItem>
                  <SelectItem value="purchase">Alış</SelectItem>
                  <SelectItem value="service_purchase">Hizmet Alışı</SelectItem>
                  <SelectItem value="collection">Tahsilat</SelectItem>
                  <SelectItem value="payment">Ödeme</SelectItem>
                  <SelectItem value="offset">Mahsup</SelectItem>
                  <SelectItem value="expense">Gider</SelectItem>
                  <SelectItem value="customer">Müşteri</SelectItem>
                  <SelectItem value="supplier">Tedarikçi</SelectItem>
                  <SelectItem value="cash_account">Kasa</SelectItem>
                  <SelectItem value="bank_account">Banka</SelectItem>
                  <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                  <SelectItem value="credit_card_payment">KK Ödemesi</SelectItem>
                  <SelectItem value="credit_card_statement">KK Ekstresi</SelectItem>
                  <SelectItem value="agenda">Ajanda</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={String(activityFilter.limit)}
                onValueChange={(val) => setActivityFilter({ ...activityFilter, limit: parseInt(val) })}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {activityLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : activityLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Tarih/Saat</TableHead>
                    <TableHead className="text-xs">Kullanıcı</TableHead>
                    <TableHead className="text-xs">İşlem</TableHead>
                    <TableHead className="text-xs">Tür</TableHead>
                    <TableHead className="text-xs">Kayıt</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Detaylar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-slate-50">
                      <TableCell className="text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          {new Date(log.created_at).toLocaleString("tr-TR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="h-3 w-3 text-slate-600" />
                          </div>
                          <span className="text-sm font-medium truncate max-w-[100px]" title={log.user_name}>
                            {log.user_name || log.user_email?.split("@")[0]}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                          {getActionLabel(log.action)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-600">{getEntityLabel(log.entity_type)}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-slate-700 truncate max-w-[120px] block" title={log.entity_name}>
                          {log.entity_name || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <div className="text-xs text-slate-500 space-y-0.5 max-w-[280px]">
                            {Object.entries(log.details).map(([key, value]) => {
                              // Format value properly
                              let displayValue = value;
                              if (typeof value === 'number') {
                                // Format numbers with commas
                                displayValue = value.toLocaleString('tr-TR');
                              } else if (typeof value === 'boolean') {
                                displayValue = value ? 'Evet' : 'Hayır';
                              } else {
                                displayValue = String(value);
                              }
                              
                              // Translate common keys
                              const keyLabels = {
                                'customer': 'Müşteri',
                                'supplier': 'Tedarikçi',
                                'total': 'Toplam',
                                'amount': 'Tutar',
                                'date': 'Tarih',
                                'category': 'Kategori',
                                'description': 'Açıklama',
                                'currency': 'Para Birimi',
                                'bank_name': 'Banka',
                                'last4': 'Son 4 Hane',
                                'period': 'Dönem',
                                'credit_card': 'Kredi Kartı',
                                'file_size': 'Dosya Boyutu',
                                'completed': 'Tamamlandı',
                                'items_count': 'Ürün Sayısı',
                              };
                              
                              const displayKey = keyLabels[key] || key;
                              
                              return (
                                <div key={key} className="truncate" title={`${displayKey}: ${displayValue}`}>
                                  <span className="font-medium text-slate-700">{displayKey}:</span>{" "}
                                  <span className="text-slate-600">{displayValue}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <History className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Henüz işlem kaydı yok</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Manuel Yedek Oluştur</AlertDialogTitle>
            <AlertDialogDescription>
              Tüm verilerinizin yedeğini almak üzeresiniz. Bu işlem birkaç saniye sürebilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={createBackup} className="bg-slate-900 hover:bg-slate-800">
              Yedek Al
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verileri İçe Aktar</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{selectedFile?.name}</strong> dosyasından veriler içe aktarılacak. 
              Mevcut veriler güncellenebilir veya yeni veriler eklenebilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedFile(null)}>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={importData} className="bg-blue-600 hover:bg-blue-700">
              İçe Aktar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Dikkat! Tüm Veriler Silinecek
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Bu işlem geri alınamaz! Tüm müşteriler, tedarikçiler, satışlar, alışlar ve diğer tüm veriler kalıcı olarak silinecektir.</p>
              <p className="font-medium">Silme işlemini onaylamak için aşağıya <span className="text-red-600 font-bold">ONAYLA</span> yazın:</p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="ONAYLA"
                className="border-red-300 focus:border-red-500"
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>İptal</AlertDialogCancel>
            <Button 
              onClick={deleteAllData} 
              disabled={deleteConfirmText !== "ONAYLA" || deleting}
              variant="destructive"
            >
              {deleting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {deleting ? "Siliniyor..." : "Tümünü Sil"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dropbox Auth Dialog */}
      <AlertDialog open={dropboxAuthDialogOpen} onOpenChange={setDropboxAuthDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              Dropbox&apos;a Bağlan
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Dropbox hesabınıza bağlanmak için aşağıdaki adımları izleyin:</p>
              
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-900 mb-2">1. Aşağıdaki butona tıklayın:</p>
                  <Button 
                    onClick={() => window.open(dropboxAuthUrl, '_blank')}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Dropbox&apos;ta Yetkilendir
                  </Button>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900 mb-2">2. Dropbox&apos;ta izin verin ve aldığınız kodu buraya yapıştırın:</p>
                  <Input
                    value={dropboxAuthCode}
                    onChange={(e) => setDropboxAuthCode(e.target.value)}
                    placeholder="Yetkilendirme kodunu yapıştırın"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDropboxAuthCode("")}>İptal</AlertDialogCancel>
            <Button onClick={submitDropboxAuth} disabled={!dropboxAuthCode.trim()} className="bg-blue-600 hover:bg-blue-700">
              Bağlantıyı Tamamla
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Backup;
